import Logo from './Logo';
import React, { useState } from 'react';
import { LuFileText, LuLayoutDashboard, LuLogOut } from 'react-icons/lu';
import {
  handleCapitalize,
  useHandleAppStateChange,
  useCheckUserRole,
} from '../contexts/Utils';
import { useLocation, useNavigate } from 'react-router-dom';
import { Avatar, Button, Divider, Menu, Select } from 'antd';
import authAtom from '../atoms/auth/auth.atom';
import { useRecoilValue } from 'recoil';
import { PiHeadset, PiMoneyWavyLight, PiPlugCharging } from 'react-icons/pi';
import { BsBuildings, BsGear } from 'react-icons/bs';
import { HiOutlineUserGroup, HiOutlineUsers } from 'react-icons/hi2';
// import { AiOutlineSwap } from 'react-icons/ai';
import { TbLayoutSidebarRightInactive } from 'react-icons/tb';
import useGetAction from '../actions/get-actions';
import appAtom from '../atoms/auth/app.atom';

type Props = {
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
};
function SideBar({ isOpen, onOpen, onClose }: Props) {
  const { pathname } = useLocation();
  const { user } = useRecoilValue(authAtom);
  const userRole = user?.role?.slug;
  const { isSuperAdminLevel, isCompanyLevel, isEstateLevel } =
    useCheckUserRole();
  const { company_id, estate_id } = useRecoilValue(appAtom);
  const [selectedCompany, setSelectedCompany] = useState<{
    value: string;
    label: string;
  } | null>(company_id || null);
  const [selectedEstate, setSelectedEstate] = useState<{
    value: string;
    label: string;
  } | null>(estate_id || null);

  const handleOpen = () => (isOpen ? onClose() : onOpen());

  // const accessibleEstateWithinCompany = user.estates.find((estate)=>{
  //   return  estate?.company?.uuid === company_id?.value
  // })

  const { data: companiesData, isLoading: companiesLoading } = useGetAction({
    url: '/admin/companies',
    keys: ['company_1'],
    funcKey: 'get_all_companies_1',
  });
  const { data: estatesData, isLoading: estatesLoading } = useGetAction({
    url: `/admin/estates`,
    params: { company: selectedCompany?.value ?? undefined },
    keys: ['estate_1'],
    funcKey: 'get_all_estates_1',
  });
  const companies = companiesData?.data?.data.map((company) => ({
    value: company.uuid,
    label: company.name,
  }));
  const estates = estatesData?.data?.data?.map((estate) => ({
    value: estate.uuid,
    label: estate.name,
  }));
  const { changeCompany, changeEstate } = useHandleAppStateChange();

  const navigate = useNavigate();
  const items = [
    {
      key: '/',
      label: 'Dashboard',
      icon: <LuLayoutDashboard className="!text-lg" />,
    },
    {
      key: 'est-mgt',
      label: 'Estate Management',
      icon: <BsBuildings className="!text-lg" />,
      children: [
        isSuperAdminLevel() && {
          key: '/companies',
          label: 'Companies',
        },
        (isSuperAdminLevel() || isCompanyLevel()) && {
          key: '/estates',
          label: 'Estates',
        },
        { key: '/properties', label: 'Property Units' },
        { key: '/wallet-fund', label: 'Wallet Funding' },
      ],
    },
    {
      key: 'bill-mgt',
      label: 'Billing Management',
      icon: <PiMoneyWavyLight className="!text-lg" />,
      children: [
        { key: '/bill-items', label: 'Billable Items' },
        { key: '/bill-templates', label: 'Billing Templates' },
        { key: '/bill-invoices', label: 'Invoices' },
      ],
    },
    {
      key: 'vend-mgt',
      label: 'Vending Management',
      icon: <PiPlugCharging className="!text-lg" />,
      children: [
        { key: '/vend-unit', label: 'Vending (Property Unit)' },
        { key: '/vend-area', label: 'Vending (Common Areas)' },
        { key: '/emergency-token', label: 'Emergency Token' },
        { key: '/tamper-code', label: 'Tamper Code' },
      ],
    },
    {
      key: 'visitor-mgt',
      label: 'Visitor Management',
      icon: <HiOutlineUsers className="!text-lg" />,
      children: [
        { key: '/visitor-logs', label: 'Visitor Logs' },
        { key: '/security-staffs', label: 'Security Staffs' },
      ],
    },
    {
      key: '/reports',
      label: 'Reports',
      icon: <LuFileText className="!text-lg" />,
    },
    // {
    //   key: '/transactions',
    //   label: 'Unit Transaction history',
    //   icon: <AiOutlineSwap className="!text-lg" />,
    // },
    {
      key: '/users-mgt',
      label: 'Users Management',
      icon: <HiOutlineUserGroup className="!text-lg" />,
    },
  ];

  const extras = [
    {
      key: '/settings',
      label: 'Settings',
      icon: <BsGear className="!text-lg" />,
    },
    {
      key: '/support',
      label: 'Support',
      icon: <PiHeadset className="!text-lg" />,
    },
  ];

  const handleMenu = ({ key }: { key: string }) => navigate(key);
  const handleLogout = () => navigate('/auth/logout');

  const handleSelect = ({
    target,
    value,
  }: {
    target: string;
    value: { value: string; label: string } | null;
  }) => {
    if (target == 'company') {
      changeCompany(value);
    } else {
      changeEstate(value);
    }
  };
  const MenuContent = () => (
    <div className="w-full h-full overflow-y-auto no-scrollbar flex flex-col justify-between items-center px-2 relative">
      <div className="w-full flex flex-col items-center gap-3 flex-1">
        <div className="w-full flex items-center gap-5 sticky top-0 z-10 bg-white pt-5 pb-3 px-3 border-b border-gray/20">
          <Logo className="!w-[50px]" path="/" />
          <div hidden={isOpen}>
            <p className="text-base font-medium text-dark">Estate Plus</p>
            <p className="text-xs font-normal text-gray">Estate Plus</p>
          </div>
          <Button
            onClick={handleOpen}
            className={`!rounded-md !z-30 ${isOpen && 'absolute top-10 -right-5 '}`}
            size="small"
            icon={
              <TbLayoutSidebarRightInactive className="!text-lg rotate-180" />
            }
          />
        </div>
        {(userRole === 'super-admin' || isCompanyLevel()) && (
          <div className="flex flex-col justify-center items-start gap-2 w-full">
            <span className="text-gray text-sm truncate ">
              Select Company to view
            </span>
            <Select
              labelInValue
              options={companies || null}
              size="large"
              className={`w-full placeholder:!text-sm`}
              placeholder="Select Company to view"
              loading={companiesLoading}
              value={selectedCompany ? selectedCompany : undefined}
              onChange={(value, option) => {
                if (!value) {
                  setSelectedCompany(null);
                  handleSelect({ target: 'company', value: null });
                } else {
                  const valObject = {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    label: (option as any)?.label,

                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    value: (option as any)?.value,
                  };
                  setSelectedCompany(valObject);
                  setSelectedEstate(null);
                  handleSelect({ target: 'company', value: valObject });
                  handleSelect({ target: 'estate', value: null });
                }
              }}
              disabled={(isCompanyLevel() || isEstateLevel()) && company_id}
              onClear={() => {
                handleSelect({ target: 'estate', value: null });
                setSelectedEstate(null);
              }}
              allowClear
            />
          </div>
        )}
        <Divider className="!m-0" />
        <div className="flex flex-col justify-center items-start gap-2 w-full">
          <span className="text-gray text-sm truncate">
            Select Estate to view
          </span>
          <Select
            labelInValue
            options={
              estates
                ? isEstateLevel()
                  ? estates.filter((estate) =>
                      user.estates.some(
                        (userEstate) => userEstate.uuid === estate.value
                      )
                    )
                  : estates
                : []
            }
            size="large"
            className={`w-full placeholder:!text-sm`}
            placeholder="Select Estate to view"
            disabled={!selectedCompany}
            loading={estatesLoading}
            value={selectedEstate ? selectedEstate : undefined}
            onChange={(value, option) => {
              if (!value) {
                setSelectedEstate(null);
                handleSelect({ target: 'estate', value: null });
              } else {
                const valObject = {
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  label: (option as any)?.label,
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  value: (option as any)?.value,
                };
                setSelectedEstate(valObject);
                handleSelect({ target: 'estate', value: valObject });
              }
            }}
            allowClear
          />
        </div>

        <Divider className="!m-2" />
        <div className="w-full max-h-[65vh] overflow-y-auto scrollbar-none">
          <Menu
            style={{
              border: 0,
              color: '#525866',
              fontSize: '14px',
              fontWeight: '400',
              background: 'transparent',
            }}
            defaultSelectedKeys={[pathname]}
            defaultOpenKeys={[pathname]}
            inlineCollapsed={isOpen}
            onClick={handleMenu}
            key="main-menu"
            mode="inline"
            items={items}
          />
        </div>
      </div>

      <div className='flex flex-col gap-3'>
        <div className="w-full flex flex-col justify-center items-center mt-5">
          <Menu
            style={{
              border: 0,
              color: '#525866',
              fontSize: '14px',
              fontWeight: '400',
              background: 'transparent',
            }}
            defaultSelectedKeys={[pathname]}
            defaultOpenKeys={[pathname]}
            inlineCollapsed={isOpen}
            onClick={handleMenu}
            key="extra-menu"
            items={extras}
            mode="inline"
          />
          {/* <Divider className="!my-2" /> */}
        </div>
        <div
          className={'w-full flex items-center px-3 sticky bottom-0 bg-white border-t border-gray/20 py-3 '.concat(
            isOpen ? 'justify-center' : 'justify-between'
          )}
        >
          <div className="flex items-center gap-3">
            <Avatar alt="user" size="large" src={user?.profile_img}>
              {handleCapitalize(user?.name?.[0])}
            </Avatar>
            <div hidden={isOpen} className="w-[120px]">
              <p title={user?.name} className="text-dark text-sm truncate">
                {user?.name || 'FM User'}
              </p>
              <p title={user?.email} className="text-gray text-xs truncate">
                {user?.email || 'fm-user@estateplus.com.ng'}
              </p>
            </div>
          </div>
          <Button
            type="text"
            title="Logout"
            hidden={isOpen}
            onClick={handleLogout}
            className="text-secondary !p-0 !m-0"
            icon={<LuLogOut className="!text-lg !font-medium" />}
          />
        </div>
      </div>
    </div>
  );
  return (
    <div className="h-full shadow bg-white border-r border-outline !w-auto">
      <div className="hidden md:block w-full h-full max-w-[300px]">
        <MenuContent />
      </div>
    </div>
  );
}

export default SideBar;
